import {TLBounds, TLTransformInfo, Utils, TLPageState, TLPage} from '@tldraw/core'
import {
  TDSnapshot,
  ShapeStyles,
  ShapesWithProp,
  TDShape,
  TDBinding,
  TldrawPatch,
  TDShapeType,
  ArrowShape,
  TDShapeUtil,
  TDPage,
  TldrawCommand, WhiteboardShape, WhiteboardStore
} from '@modules/whiteboard'
import {Vec} from '@tldraw/vec'

export class TLDR {
  static getSelectedShapes(app: WhiteboardStore, pageId: string) {
    const page = TLDR.getPage(app, pageId)
    const selectedIds = TLDR.getSelectedIds(app, pageId)
    return selectedIds.map((id) => page.shapes[id])
  }

  static screenToWorld(app: WhiteboardStore, point: number[]) {
    const camera = TLDR.getPageState(app, app.ui.currentPageId).camera
    return Vec.sub(Vec.div(point, camera.zoom), camera.point)
  }

  static getCameraZoom(zoom: number) {
    return Utils.clamp(zoom, 0.1, 5)
  }

  static getPage(app: WhiteboardStore, pageId: string): TLPage<WhiteboardShape, TDBinding> {
    return app.pageById[pageId]?.page
  }

  static getPageState(app: WhiteboardStore, pageId: string): TLPageState {
    return app.pageState as TLPageState
  }

  static getSelectedIds(app: WhiteboardStore, pageId: string): string[] {
    return app.selectedIds
  }

  static getShapes(app: WhiteboardStore, pageId: string): TDShape[] {
    return app.getShapes(pageId)
  }

  static getCamera(app: WhiteboardStore, pageId: string): TLPageState['camera'] {
    return TLDR.getPageState(app, pageId).camera
  }

  static getShape<T extends TDShape = TDShape>(
    app: WhiteboardStore,
    shapeId: string,
    pageId: string
  ): T {
    return TLDR.getPage(app, pageId).shapes[shapeId] as T
  }

  static getParentId(app: WhiteboardStore, id: string, pageId: string) {
    return TLDR.getShape(app, id, pageId).parentId
  }

  // static getPointedId(app: WhiteboardStore, id: string, pageId: string): string {
  //   const page = TLDR.getPage(app, pageId)
  //   const pageState = TLDR.getPageState(app, data.appState.currentPageId)
  //   const shape = TLDR.getShape(app, id, pageId)
  //   if (!shape) return id

  //   return shape.parentId === pageState.currentParentId || shape.parentId === page.id
  //     ? id
  //     : TLDR.getPointedId(app, shape.parentId, pageId)
  // }

  // static getDrilledPointedId(app: WhiteboardStore, id: string, pageId: string): string {
  //   const shape = TLDR.getShape(app, id, pageId)
  //   const { currentPageId } = data.appState
  //   const { currentParentId, pointedId } = TLDR.getPageState(app, data.appState.currentPageId)

  //   return shape.parentId === currentPageId ||
  //     shape.parentId === pointedId ||
  //     shape.parentId === currentParentId
  //     ? id
  //     : TLDR.getDrilledPointedId(app, shape.parentId, pageId)
  // }

  // static getTopParentId(app: WhiteboardStore, id: string, pageId: string): string {
  //   const page = TLDR.getPage(app, pageId)
  //   const pageState = TLDR.getPageState(app, pageId)
  //   const shape = TLDR.getShape(app, id, pageId)

  //   if (shape.parentId === shape.id) {
  //     throw Error(`Shape has the same id as its parent! ${shape.id}`)
  //   }

  //   return shape.parentId === page.id || shape.parentId === pageState.currentParentId
  //     ? id
  //     : TLDR.getTopParentId(app, shape.parentId, pageId)
  // }

  // Get an array of a shape id and its descendant shapes' ids
  static getDocumentBranch(app: WhiteboardStore, id: string, pageId: string): string[] {
    return app.getDocumentBranch(id, pageId)
  }

  // Get a deep array of unproxied shapes and their descendants
  static getSelectedBranchSnapshot<K>(
    app: WhiteboardStore,
    pageId: string,
    fn: (shape: TDShape) => K
  ): ({ id: string } & K)[]
  static getSelectedBranchSnapshot(app: WhiteboardStore, pageId: string): TDShape[]
  static getSelectedBranchSnapshot<K>(
    app: WhiteboardStore,
    pageId: string,
    fn?: (shape: TDShape) => K
  ): (TDShape | K)[] {
    const page = TLDR.getPage(app, pageId)

    const copies = TLDR.getSelectedIds(app, pageId)
      .flatMap((id) => TLDR.getDocumentBranch(app, id, pageId).map((id) => page.shapes[id]))
      .filter((shape) => !shape.isLocked)
      .map(Utils.deepClone)

    if (fn !== undefined) {
      return copies.map((shape) => ({id: shape.id, ...fn(shape)}))
    }

    return copies
  }

  // Get a shallow array of unproxied shapes
  static getSelectedShapeSnapshot(app: WhiteboardStore, pageId: string): TDShape[]
  static getSelectedShapeSnapshot<K>(
    app: WhiteboardStore,
    pageId: string,
    fn?: (shape: TDShape) => K
  ): ({ id: string } & K)[]
  static getSelectedShapeSnapshot<K>(
    app: WhiteboardStore,
    pageId: string,
    fn?: (shape: TDShape) => K
  ): (TDShape | K)[] {
    const copies = TLDR.getSelectedShapes(app, pageId)
      .filter((shape) => !shape.isLocked)
      .map(Utils.deepClone)

    if (fn !== undefined) {
      return copies.map((shape) => ({id: shape.id, ...fn(shape)}))
    }

    return copies
  }

  // For a given array of shape ids, an array of all other shapes that may be affected by a mutation to it.
  // Use this to decide which shapes to clone as before / after for a command.
  static getAllEffectedShapeIds(app: WhiteboardStore, ids: string[], pageId: string): string[] {
    const page = TLDR.getPage(app, pageId)

    const visited = new Set(ids)

    ids.forEach((id) => {
      const shape = page.shapes[id]

      // Add descendant shapes
      function collectDescendants(shape: TDShape): void {
        if (shape.children === undefined) return
        shape.children
          .filter((childId) => !visited.has(childId))
          .forEach((childId) => {
            visited.add(childId)
            collectDescendants(page.shapes[childId])
          })
      }

      collectDescendants(shape)

      // Add asecendant shapes
      function collectAscendants(shape: TDShape): void {
        const parentId = shape.parentId
        if (parentId === page.id) return
        if (visited.has(parentId)) return
        visited.add(parentId)
        collectAscendants(page.shapes[parentId])
      }

      collectAscendants(shape)

      // Add bindings that are to or from any of the visited shapes (this does not have to be recursive)
      visited.forEach((id) => {
        Object.values(page.bindings)
          .filter((binding) => binding.fromId === id || binding.toId === id)
          .forEach((binding) => visited.add(binding.fromId === id ? binding.toId : binding.fromId))
      })
    })

    // Return the unique array of visited shapes
    return Array.from(visited.values())
  }



  static getLinkedShapeIds(
    app: WhiteboardStore,
    pageId: string,
    direction: 'center' | 'left' | 'right',
    includeArrows = true
  ) {
    const selectedIds = TLDR.getSelectedIds(app, pageId)

    const page = TLDR.getPage(app, pageId)

    const linkedIds = new Set<string>(selectedIds)

    const checkedIds = new Set<string>()

    const idsToCheck = [...selectedIds]

    const arrows = new Set(
      Object.values(page.shapes).filter((shape) => {
        return (
          shape.type === 'arrow' &&
          (shape.handles.start.bindingId || shape.handles?.end.bindingId)
        )
      }) as ArrowShape[]
    )

    while (idsToCheck.length) {
      const id = idsToCheck.pop()

      if (!(id && arrows.size)) break

      if (checkedIds.has(id)) continue

      checkedIds.add(id)

      arrows.forEach((arrow) => {
        const {
          handles: {
            start: {bindingId: startBindingId},
            end: {bindingId: endBindingId}
          }
        } = arrow

        const startBinding = startBindingId ? page.bindings[startBindingId] : null
        const endBinding = endBindingId ? page.bindings[endBindingId] : null

        let hit = false

        if (startBinding && startBinding.toId === id) {
          if (direction === 'center') {
            hit = true
          } else if (arrow.decorations?.start && endBinding) {
            // The arrow is pointing to this shape at its start
            hit = direction === 'left'
          } else {
            // The arrow is pointing away from this shape
            hit = direction === 'right'
          }

          if (hit) {
            // This arrow is bound to this shape
            if (includeArrows) linkedIds.add(arrow.id)
            linkedIds.add(id)

            if (endBinding) {
              linkedIds.add(endBinding.toId)
              idsToCheck.push(endBinding.toId)
            }
          }
        } else if (endBinding && endBinding.toId === id) {
          // This arrow is bound to this shape at its end
          if (direction === 'center') {
            hit = true
          } else if (arrow.decorations?.end && startBinding) {
            // The arrow is pointing to this shape
            hit = direction === 'left'
          } else {
            // The arrow is pointing away from this shape
            hit = direction === 'right'
          }

          if (hit) {
            if (includeArrows) linkedIds.add(arrow.id)
            linkedIds.add(id)

            if (startBinding) {
              linkedIds.add(startBinding.toId)
              idsToCheck.push(startBinding.toId)
            }
          }
        }

        if (
          (!startBinding || linkedIds.has(startBinding.toId)) &&
          (!endBinding || linkedIds.has(endBinding.toId))
        ) {
          arrows.delete(arrow)
        }
      })
    }

    return Array.from(linkedIds.values())
  }

  static getChildIndexAbove(app: WhiteboardStore, id: string, pageId: string): number {
    return app.getChildIndexAbove(id, pageId)
  }

  /* -------------------------------------------------- */
  /*                      Mutations                     */

  /* -------------------------------------------------- */

  static getBeforeShape<T extends TDShape>(shape: T, change: Partial<T>): Partial<T> {
    return Object.fromEntries(
      Object.keys(change).map((k) => [k, shape[k as keyof T]])
    ) as Partial<T>
  }

  static mutateShapes<T extends TDShape>(
    app: WhiteboardStore,
    ids: string[],
    fn: (shape: T, i: number) => Partial<T> | void,
    pageId: string
  ): {
    before: Record<string, Partial<T>>
    after: Record<string, Partial<T>>
    data: TDSnapshot
  } {
    const beforeShapes: Record<string, Partial<T>> = {}
    const afterShapes: Record<string, Partial<T>> = {}

    ids.forEach((id, i) => {
      const shape = TLDR.getShape<T>(app, id, pageId)
      if (shape.isLocked) return

      const change = fn(shape, i)
      if (change) {
        const beforeShape = TLDR.getBeforeShape(shape, change)
        if (beforeShape) {
          beforeShapes[id] = beforeShape
        }

        afterShapes[id] = change
      }
    })

    const dataWithMutations: TDSnapshot = Utils.deepMerge(app.settings, {
      pages: {
        [app.currentPageId]: {
          shapes: afterShapes
        }
      }
    })
    const dataWithBindingChanges = ids.reduce<TDSnapshot>((cTDSnapshot, id) => {
      return app.shapeUtil.updateBindings(id, beforeShapes, afterShapes)
    }, dataWithMutations)

    return {
      before: beforeShapes,
      after: afterShapes,
      data: dataWithBindingChanges
    }
  }

  static createShapes(app: WhiteboardStore, shapes: TDShape[], pageId: string): TldrawCommand {
    const before: TldrawPatch = {
      document: {
        pages: {
          [pageId]: {
            shapes: {
              ...Object.fromEntries(
                shapes.flatMap((shape) => {
                  const results: [string, Partial<TDShape> | undefined][] = [[shape.id, undefined]]

                  // If the shape is a child of another shape, also save that shape
                  if (shape.parentId !== pageId) {
                    const parent = TLDR.getShape(app, shape.parentId, pageId)
                    if (!parent.children) throw Error('No children in parent!')
                    results.push([parent.id, {children: parent.children}])
                  }

                  return results
                })
              )
            }
          }
        }
      }
    }

    const after: TldrawPatch = {
      document: {
        pages: {
          [pageId]: {
            shapes: {
              shapes: {
                ...Object.fromEntries(
                  shapes.flatMap((shape) => {
                    const results: [string, Partial<TDShape> | undefined][] = [[shape.id, shape]]

                    // If the shape is a child of a different shape, update its parent
                    if (shape.parentId !== pageId) {
                      const parent = TLDR.getShape(app, shape.parentId, pageId)
                      if (!parent.children) throw Error('No children in parent!')
                      results.push([parent.id, {children: [...parent.children, shape.id]}])
                    }

                    return results
                  })
                )
              }
            }
          }
        }
      }
    }

    return {
      before,
      after
    }
  }

  static deleteShapes(
    app: WhiteboardStore,
    shapes: TDShape[] | string[],
    pageId?: string
  ): TldrawCommand {
    pageId = pageId ? pageId : app.currentPageId

    const page = TLDR.getPage(app, pageId)

    const shapeIds =
      typeof shapes[0] === 'string'
      ? (shapes as string[])
      : (shapes as TDShape[]).map((shape) => shape.id)

    const before: TldrawPatch = {
      document: {
        pages: {
          [pageId]: {
            shapes: {
              // These are the shapes that we're going to delete
              ...Object.fromEntries(
                shapeIds.flatMap((id) => {
                  const shape = page.shapes[id]
                  const results: [string, Partial<TDShape> | undefined][] = [[shape.id, shape]]

                  // If the shape is a child of another shape, also add that shape
                  if (shape.parentId !== pageId) {
                    const parent = page.shapes[shape.parentId]
                    if (!parent.children) throw Error('No children in parent!')
                    results.push([parent.id, {children: parent.children}])
                  }

                  return results
                })
              )
            },
            bindings: {
              // These are the bindings that we're going to delete
              ...Object.fromEntries(
                Object.values(page.bindings)
                  .filter((binding) => {
                    return shapeIds.includes(binding.fromId) || shapeIds.includes(binding.toId)
                  })
                  .map((binding) => {
                    return [binding.id, binding]
                  })
              )
            }
          }
        }
      }
    }

    const after: TldrawPatch = {
      document: {
        pages: {
          [pageId]: {
            shapes: {
              ...Object.fromEntries(
                shapeIds.flatMap((id) => {
                  const shape = page.shapes[id]
                  const results: [string, Partial<TDShape> | undefined][] = [[shape.id, undefined]]

                  // If the shape is a child of a different shape, update its parent
                  if (shape.parentId !== page.id) {
                    const parent = page.shapes[shape.parentId]

                    if (!parent.children) throw Error('No children in parent!')

                    results.push([
                      parent.id,
                      {children: parent.children.filter((id) => id !== shape.id)}
                    ])
                  }

                  return results
                })
              )
            }
          }
        }
      }
    }

    return {
      before,
      after
    }
  }



  /* -------------------------------------------------- */
  /*                       Parents                      */

  /* -------------------------------------------------- */

  static updateParents(app: WhiteboardStore, pageId: string, changedShapeIds: string[]): void {
    const page = TLDR.getPage(app, pageId)

    if (changedShapeIds.length === 0) return

    const {shapes} = TLDR.getPage(app, pageId)

    const parentToUpdateIds = Array.from(
      new Set(changedShapeIds.map((id) => shapes[id].parentId).values())
    ).filter((id) => id !== page.id)

    for (const parentId of parentToUpdateIds) {
      const parent = shapes[parentId]

      if (!parent.children) {
        throw Error('A shape is parented to a shape without a children array.')
      }

      app.shapeUtil.onChildrenChange(parent)
    }

    TLDR.updateParents(app, pageId, parentToUpdateIds)
  }

  /* -------------------------------------------------- */
  /*                      Bindings                      */

  /* -------------------------------------------------- */

  static getBinding(app: WhiteboardStore, id: string, pageId: string): TDBinding {
    return TLDR.getPage(app, pageId).bindings[id]
  }

  static getBindings(app: WhiteboardStore, pageId: string): TDBinding[] {
    const page = TLDR.getPage(app, pageId)
    return Object.values(page.bindings)
  }

  static getBindableShapeIds(app: WhiteboardStore) {
    return TLDR.getShapes(app, app.currentPageId)
      .filter((shape) => app.shapeUtilFor(shape).canBind)
      .sort((a, b) => b.childIndex - a.childIndex)
      .map((shape) => shape.id)
  }

  static getBindingsWithShapeIds(app: WhiteboardStore, ids: string[], pageId: string): TDBinding[] {
    return Array.from(
      new Set(
        TLDR.getBindings(app, pageId).filter((binding) => {
          return ids.includes(binding.toId) || ids.includes(binding.fromId)
        })
      ).values()
    )
  }

  static getRelatedBindings(app: WhiteboardStore, ids: string[], pageId: string): TDBinding[] {
    const changedShapeIds = new Set(ids)

    const page = TLDR.getPage(app, pageId)

    // Find all bindings that we need to update
    const bindingsArr = Object.values(page.bindings)

    // Start with bindings that are directly bound to our changed shapes
    const bindingsToUpdate = new Set(
      bindingsArr.filter(
        (binding) => changedShapeIds.has(binding.toId) || changedShapeIds.has(binding.fromId)
      )
    )

    // Next, look for other bindings that effect the same shapes
    let prevSize = bindingsToUpdate.size
    let delta = -1

    while (delta !== 0) {
      bindingsToUpdate.forEach((binding) => {
        const fromId = binding.fromId

        for (const otherBinding of bindingsArr) {
          if (otherBinding.fromId === fromId) {
            bindingsToUpdate.add(otherBinding)
          }

          if (otherBinding.toId === fromId) {
            bindingsToUpdate.add(otherBinding)
          }
        }
      })

      // Continue until we stop finding new bindings to update
      delta = bindingsToUpdate.size - prevSize

      prevSize = bindingsToUpdate.size
    }

    return Array.from(bindingsToUpdate.values())
  }

  static copyStringToClipboard = (string: string) => {
    try {
      navigator.clipboard.writeText(string)
    } catch (e) {
      const textarea = document.createElement('textarea')
      textarea.setAttribute('position', 'fixed')
      textarea.setAttribute('top', '0')
      textarea.setAttribute('readonly', 'true')
      textarea.setAttribute('contenteditable', 'true')
      textarea.style.position = 'fixed'
      textarea.value = string
      document.body.appendChild(textarea)
      textarea.focus()
      textarea.select()

      try {
        const range = document.createRange()
        range.selectNodeContents(textarea)
        const sel = window.getSelection()
        if (sel) {
          sel.removeAllRanges()
          sel.addRange(range)
          textarea.setSelectionRange(0, textarea.value.length)
        }
      } catch (err) {
        null // Could not copy to clipboard
      } finally {
        document.body.removeChild(textarea)
      }
    }
  }

  /* -------------------------------------------------- */
  /*                       Groups                       */
  /* -------------------------------------------------- */

  static flattenShape = (app: WhiteboardStore, shape: TDShape): TDShape[] => {
    return app.flattenShape(shape)
  }

  static flattenPage = (app: WhiteboardStore, pageId: string): TDShape[] => {
    return app.flattenPage(pageId)
  }

  static getTopChildIndex = (app: WhiteboardStore, pageId: string): number => {
    return app.getTopChildIndex(pageId)
  }

  /* -------------------------------------------------- */
  /*                        Text                        */
  /* -------------------------------------------------- */

  static fixNewLines = /\r?\n|\r/g

  static normalizeText(text: string) {
    return text.replace(TLDR.fixNewLines, '\n')
  }

  /* -------------------------------------------------- */
  /*                     Assertions                     */

  /* -------------------------------------------------- */

  static assertShapeHasProperty<P extends keyof TDShape>(
    shape: TDShape,
    prop: P
  ): asserts shape is ShapesWithProp<P> {
    if (shape[prop] === undefined) {
      throw new Error()
    }
  }
}



// WEBPACK FOOTER //
// ./src/modules/whiteboard/TLDR.ts